import React, { createContext, useCallback, useContext } from 'react';

import invariant from 'tiny-invariant';

import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';

import type {
	CollapseCallback,
	ExpandCallback,
	ResizeEndCallback,
	ResizeStartCallback,
	SideNavVisibilityContextType,
	SideNavVisibilityProviderProps,
} from './types';

export const SideNavVisibilityContext = createContext<SideNavVisibilityContextType | null>(null);

/*
 * useSideNavVisibility - Returns Visibility callbacks from Navigation System
 */
export const useSideNavVisibility = () => {
	const context = useContext(SideNavVisibilityContext);
	invariant(context, 'useSideNavVisibility must be used within an SideNavVisibilityProvider');

	return context;
};

/*
 *  SideNavVisibilityProvider - Wrapper for the Navigation System visibility callbacks.
 */
export function SideNavVisibilityProvider({
	children,
	onCollapse,
	onExpand,
	onResizeEnd,
	onResizeStart,
	setUserSideNavData,
}: SideNavVisibilityProviderProps) {
	const { fireEvent } = useAnalyticsEvents();

	const onCollapseHandler = useCallback<CollapseCallback>(
		(args) => {
			fireEvent('ui.sideNav.collapsed', { ...args });
			setUserSideNavData({ defaultCollapsed: true });
			onCollapse?.(args);
		},
		[fireEvent, onCollapse, setUserSideNavData],
	);

	const onExpandHandler = useCallback<ExpandCallback>(
		(args) => {
			fireEvent('ui.sideNav.expanded', { ...args });
			setUserSideNavData({ defaultCollapsed: false });
			onExpand?.(args);
		},
		[fireEvent, onExpand, setUserSideNavData],
	);

	const onResizeStartHandler = useCallback<ResizeStartCallback>(
		(args) => {
			fireEvent('ui.sideNav.resizeStart', { ...args });
			onResizeStart?.(args);
		},
		[fireEvent, onResizeStart],
	);

	const onResizeEndHandler = useCallback<ResizeEndCallback>(
		(args) => {
			fireEvent('ui.sideNav.resizeEnd', { ...args });
			setUserSideNavData({ defaultWidth: args.finalWidth });
			onResizeEnd?.(args);
		},
		[fireEvent, onResizeEnd, setUserSideNavData],
	);

	return (
		<SideNavVisibilityContext.Provider
			value={{
				onExpand: onExpandHandler,
				onCollapse: onCollapseHandler,
				onResizeEnd: onResizeEndHandler,
				onResizeStart: onResizeStartHandler,
			}}
		>
			{children}
		</SideNavVisibilityContext.Provider>
	);
}
